import { Stats, RawEvent, Decision, CoverageDocument, CoverageCategory, ActivityItem, WorkflowStatus, Tenant, TenantStatus, SourceStats, Alert, TrendPoint, AlertChannel, ScatterPoint, Org, Agent, ManagedUser, AgentConfig, ROIAnalytics, ROIConfig, AgentAPIKey, CreateAgentAPIKeyResponse } from '@/types';

const API_BASE = '';

class ApiService {
  private tenant: string = 'klm';
  private token: string | null = null;
  private onUnauthorized: (() => void) | null = null;
  private refreshing: Promise<boolean> | null = null;

  setAccessToken(token: string | null) {
    this.token = token;
  }

  setOnUnauthorized(handler: () => void) {
    this.onUnauthorized = handler;
  }

  setTenant(tenant: string) {
    this.tenant = tenant;
  }

  getTenant(): string {
    return this.tenant;
  }

  /** Attempt to refresh the access token. Returns true on success. */
  private async tryRefresh(): Promise<boolean> {
    // Deduplicate concurrent refresh attempts
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      try {
        const refreshToken = localStorage.getItem('sentinel_refresh_token');
        if (!refreshToken) return false;
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        this.token = data.access_token;
        localStorage.setItem('sentinel_access_token', data.access_token);
        localStorage.setItem('sentinel_refresh_token', data.refresh_token);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    const h: Record<string, string> = { ...extra };
    if (this.token) {
      h['Authorization'] = `Bearer ${this.token}`;
    }
    return h;
  }

  private async request<T>(url: string, opts: RequestInit = {}): Promise<T> {
    const res = await fetch(url, {
      ...opts,
      headers: { ...this.headers(), ...((opts.headers as Record<string, string>) || {}) },
    });
    if (res.status === 401) {
      // Try refreshing the token before giving up
      if (await this.tryRefresh()) {
        const retry = await fetch(url, {
          ...opts,
          headers: { ...this.headers(), ...((opts.headers as Record<string, string>) || {}) },
        });
        if (retry.ok) return retry.json();
      }
      this.onUnauthorized?.();
      throw new Error('Unauthorized');
    }
    if (res.status === 403) {
      throw new Error('Forbidden');
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message = text || `Request failed (${res.status})`;
      try {
        const parsed = JSON.parse(text);
        if (parsed.error) message = parsed.error;
      } catch { /* not JSON, use raw text */ }
      throw new Error(message);
    }
    return res.json();
  }

  private async requestVoid(url: string, opts: RequestInit = {}): Promise<void> {
    const res = await fetch(url, {
      ...opts,
      headers: { ...this.headers(), ...((opts.headers as Record<string, string>) || {}) },
    });
    if (res.status === 401) {
      if (await this.tryRefresh()) {
        const retry = await fetch(url, {
          ...opts,
          headers: { ...this.headers(), ...((opts.headers as Record<string, string>) || {}) },
        });
        if (!retry.ok && retry.status !== 401) throw new Error(`Request failed (${retry.status})`);
        if (retry.ok) return;
      }
      this.onUnauthorized?.();
      throw new Error('Unauthorized');
    }
    if (res.status === 403) throw new Error('Forbidden');
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
  }

  async getTenants(): Promise<Tenant[]> {
    return this.request(`${API_BASE}/api/tenants`);
  }

  async getStats(): Promise<Stats> {
    return this.request(`${API_BASE}/api/stats?tenant=${this.tenant}`);
  }

  async getEvents(includeExpired: boolean = false): Promise<RawEvent[]> {
    const expiredParam = includeExpired ? '&include_expired=true' : '';
    return this.request(`${API_BASE}/api/events?tenant=${this.tenant}${expiredParam}`);
  }

  async getDecisions(relevant?: boolean, limit: number = 100, includeExpired: boolean = false): Promise<Decision[]> {
    const relevantParam = relevant !== undefined ? `&relevant=${relevant}` : '';
    const expiredParam = includeExpired ? '&include_expired=true' : '';
    return this.request(`${API_BASE}/api/decisions?tenant=${this.tenant}${relevantParam}&limit=${limit}${expiredParam}`);
  }

  async getActivity(minutes: number = 60): Promise<ActivityItem[]> {
    return this.request(`${API_BASE}/api/activity?tenant=${this.tenant}&minutes=${minutes}`);
  }

  async getCoverageDocuments(includeExpired: boolean = false): Promise<CoverageDocument[]> {
    const expiredParam = includeExpired ? '&include_expired=true' : '';
    return this.request(`${API_BASE}/api/coverage?tenant=${this.tenant}${expiredParam}`);
  }

  async getCoverageDocument(id: string): Promise<CoverageDocument> {
    return this.request(`${API_BASE}/api/coverage/${id}`);
  }

  async updateCoverageDocument(id: string, content: string): Promise<CoverageDocument> {
    return this.request(`${API_BASE}/api/coverage/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ markdown_content: content }),
    });
  }

  async updateWorkflowStatus(id: string, status: WorkflowStatus): Promise<CoverageDocument> {
    return this.request(`${API_BASE}/api/coverage/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
  }

  async regenerateDocument(id: string, guidance?: string): Promise<CoverageDocument> {
    const options: RequestInit = { method: 'POST' };
    if (guidance) {
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify({ guidance });
    }
    return this.request(`${API_BASE}/api/coverage/${id}/regenerate`, options);
  }

  async generateBriefing(): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/coverage/generate`, { method: 'POST' });
  }

  // Coverage Categories
  async getCategories(): Promise<CoverageCategory[]> {
    return this.request(`${API_BASE}/api/coverage/categories?tenant=${this.tenant}`);
  }

  async createCategory(data: Partial<CoverageCategory>): Promise<CoverageCategory> {
    return this.request(`${API_BASE}/api/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, tenant_id: this.tenant }),
    });
  }

  async updateCategory(id: string, data: Partial<CoverageCategory>): Promise<CoverageCategory> {
    return this.request(`${API_BASE}/api/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/categories/${id}`, { method: 'DELETE' });
  }

  async triggerAlert(decisionId: string): Promise<{ status: string; last_alerted_at: string }> {
    return this.request(`${API_BASE}/api/decisions/${decisionId}/alert`, { method: 'POST' });
  }

  async overrideDecision(decisionId: string, score: number, reason: string): Promise<{
    status: string;
    original_score: number;
    new_score: number;
    new_severity: string;
    new_is_relevant: boolean;
    overridden_by: string;
  }> {
    return this.request(`${API_BASE}/api/decisions/${decisionId}/override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score, reason }),
    });
  }

  async getFilteredEvents(limit: number = 50): Promise<any[]> {
    return this.request(`${API_BASE}/api/events/filtered?tenant=${this.tenant}&limit=${limit}`);
  }

  async promoteFilteredEvent(eventId: string, reason: string): Promise<{ status: string; event_id: string; message: string }> {
    return this.request(`${API_BASE}/api/events/${eventId}/promote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
  }

  async getTenantStatus(): Promise<TenantStatus[]> {
    return this.request(`${API_BASE}/api/tenants/status`);
  }

  async pauseTenant(tenantId: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/tenants/${tenantId}/pause`, { method: 'POST' });
  }

  async resumeTenant(tenantId: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/tenants/${tenantId}/resume`, { method: 'POST' });
  }

  async getSourceStats(): Promise<SourceStats[]> {
    return this.request(`${API_BASE}/api/sources/stats?tenant_id=${this.tenant}`);
  }

  async getAlerts(): Promise<Alert[]> {
    const data = await this.request<Alert[]>(`${API_BASE}/api/alerts?tenant_id=${this.tenant}`);
    return data || [];
  }

  async acknowledgeAlert(alertId: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/alerts/${alertId}/acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
  }

  async getChannels(): Promise<AlertChannel[]> {
    const data = await this.request<AlertChannel[]>(`${API_BASE}/api/channels`);
    return data || [];
  }

  async sendTestAlert(): Promise<{ status: string; channels: number; message: string }> {
    return this.request(`${API_BASE}/api/test-alert?tenant_id=${this.tenant}`, { method: 'POST' });
  }

  async getTrends(period: string = '24 hours', interval: string = '1 hour'): Promise<TrendPoint[]> {
    return this.request(`${API_BASE}/api/stats/trends?tenant=${this.tenant}&period=${encodeURIComponent(period)}&interval=${encodeURIComponent(interval)}`);
  }

  async getScatter(period: string = '3 hours'): Promise<ScatterPoint[]> {
    return this.request(`${API_BASE}/api/stats/scatter?tenant=${this.tenant}&period=${encodeURIComponent(period)}`);
  }

  async getAnalytics(period: number = 30): Promise<ROIAnalytics> {
    return this.request(`${API_BASE}/api/stats/analytics?tenant=${this.tenant}&period=${period}`);
  }

  async saveROI(config: ROIConfig): Promise<{ status: string }> {
    return this.request(`${API_BASE}/api/admin/roi?tenant=${this.tenant}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
  }

  async exportCoverage(docId: string, webhookUrl: string, format: string = 'json'): Promise<{ status: string; response_status: number }> {
    return this.request(`${API_BASE}/api/coverage/${docId}/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ webhook_url: webhookUrl, format }),
    });
  }

  getCoverageDownloadUrl(docId: string): string {
    return `${API_BASE}/api/coverage/${docId}/download`;
  }

  // ---------------------------------------------------------------
  // User-context API (accessible by all authenticated users)
  // ---------------------------------------------------------------

  async getUserVisibleAgents(): Promise<Agent[]> {
    return this.request(`${API_BASE}/api/user/agents`);
  }

  async getUserVisibleOrgs(): Promise<Org[]> {
    return this.request(`${API_BASE}/api/user/orgs`);
  }

  // ---------------------------------------------------------------
  // Admin API
  // ---------------------------------------------------------------

  async getOrgs(): Promise<Org[]> {
    return this.request(`${API_BASE}/api/admin/orgs`);
  }

  async createOrg(data: { name: string; slug: string }): Promise<Org> {
    return this.request(`${API_BASE}/api/admin/orgs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async updateOrg(id: string, data: { name?: string; slug?: string }): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/orgs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteOrg(id: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/orgs/${id}`, { method: 'DELETE' });
  }

  async getAgents(): Promise<Agent[]> {
    return this.request(`${API_BASE}/api/admin/agents`);
  }

  async createAgent(data: { org_id: string; tenant_id: string; name: string }): Promise<Agent> {
    return this.request(`${API_BASE}/api/admin/agents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async updateAgent(id: string, data: { name?: string; org_id?: string }): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/agents/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteAgent(id: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/agents/${id}`, { method: 'DELETE' });
  }

  async getAgentConfig(agentId: string): Promise<{ config: AgentConfig; version: number }> {
    return this.request(`${API_BASE}/api/admin/agents/${agentId}/config`);
  }

  async updateAgentConfig(agentId: string, config: AgentConfig, version: number): Promise<{ status: string; version: number }> {
    return this.request(`${API_BASE}/api/admin/agents/${agentId}/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ config, version }),
    });
  }

  async deleteAgentConfig(agentId: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/agents/${agentId}/config`, { method: 'DELETE' });
  }

  async generateAgentConfig(agentId: string, intake: { company_name: string; business_url: string; use_case: string; target_customer: string }): Promise<{ config: AgentConfig }> {
    return this.request(`${API_BASE}/api/admin/agents/${agentId}/generate-config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(intake),
    });
  }

  async getUsers(): Promise<ManagedUser[]> {
    return this.request(`${API_BASE}/api/admin/users`);
  }

  async createUser(data: { email: string; password: string; display_name?: string; role: string; org_id?: string; is_active?: boolean }): Promise<ManagedUser> {
    return this.request(`${API_BASE}/api/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: { display_name?: string; role?: string; is_active?: boolean; password?: string }): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/users/${id}`, { method: 'DELETE' });
  }

  async getUserAgents(userId: string): Promise<string[]> {
    return this.request(`${API_BASE}/api/admin/users/${userId}/agents`);
  }

  async setUserAgents(userId: string, agentIds: string[]): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/users/${userId}/agents`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_ids: agentIds }),
    });
  }

  // Profile
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request(`${API_BASE}/api/auth/password`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async updateProfile(displayName: string): Promise<{ display_name: string }> {
    return this.request(`${API_BASE}/api/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: displayName }),
    });
  }

  async getModels(): Promise<{ id: string; description: string }[]> {
    return this.request(`${API_BASE}/api/models`);
  }

  // Agent API Keys
  async getAPIKeys(): Promise<AgentAPIKey[]> {
    return this.request(`${API_BASE}/api/admin/api-keys`);
  }

  async createAPIKey(data: { tenant_id: string; name: string; scopes?: string[]; rate_limit?: number }): Promise<CreateAgentAPIKeyResponse> {
    return this.request(`${API_BASE}/api/admin/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async revokeAPIKey(id: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/api-keys/${id}`, { method: 'DELETE' });
  }

  // OIDC / SSO
  async getOIDCConfig(): Promise<{ enabled: boolean }> {
    return this.request(`${API_BASE}/api/auth/oidc/config`);
  }

  async oidcExchange(code: string): Promise<{ access_token: string; refresh_token: string; user: any }> {
    return this.request(`${API_BASE}/api/auth/oidc/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  }

  // Admin: approve/reject pending users
  async approveUser(id: string, data?: { role?: string; org_id?: string }): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/users/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data || {}),
    });
  }

  async rejectUser(id: string): Promise<void> {
    await this.requestVoid(`${API_BASE}/api/admin/users/${id}/reject`, { method: 'POST' });
  }

  async getPendingUsers(): Promise<ManagedUser[]> {
    return this.request(`${API_BASE}/api/admin/users?status=pending`);
  }

  // Prompt Overrides
  async getPromptOverrides(agentId: string): Promise<{ overrides: Record<string, Record<string, string>>; version: number }> {
    return this.request(`${API_BASE}/api/admin/agents/${agentId}/prompt-overrides`);
  }

  async updatePromptOverrides(agentId: string, overrides: Record<string, Record<string, string>>, version: number): Promise<{ status: string; version: number; overrides: Record<string, Record<string, string>> }> {
    return this.request(`${API_BASE}/api/admin/agents/${agentId}/prompt-overrides`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ overrides, version }),
    });
  }
}

export const api = new ApiService();
